/**
 * Vuex module — atendimento (conversas WhatsApp).
 *
 * Estado:
 *   estagios          — lista de ConversaEstagio (colunas do kanban)
 *   conversas         — lista de Conversa (com estagio + contato + atendente)
 *   conversaAtivaId   — id da conversa selecionada (drawer aberto)
 *   mensagensPorConversa — { [conversaId]: Mensagem[] }
 *   filtroMinhas      — true: mostrar só atendente_id == userId
 *
 * Actions chamam axios.* (baseURL global). Em produção autenticação via
 * cookie httpOnly; nada de token aqui.
 */

import axios from 'axios';
import notificationSound from '@/services/notificationSound';

export const state = {
  estagios: [],
  conversas: [],
  conversaAtivaId: null,
  mensagensPorConversa: {},
  // Paginação por conversa: { [conversaId]: { temMais, cursor, carregando } }
  paginacaoPorConversa: {},
  filtroMinhas: false,
  loading: false,
  // Filtros do kanban (espelham os do ContatoGrid).
  filtros: {
    busca: '',
    status: '',
    rotulosIds: [],
    estados: [],
  },
  // Mensagem que o atendente está respondendo (estilo WhatsApp reply).
  respondendoA: null,
};

// Tamanho da página de mensagens (estilo WhatsApp Web — carrega ao subir).
const PAGE_SIZE = 30;

export const mutations = {
  SET_ESTAGIOS(s, estagios) { s.estagios = Array.isArray(estagios) ? estagios : []; },
  SET_CONVERSAS(s, conversas) { s.conversas = Array.isArray(conversas) ? conversas : []; },
  UPSERT_CONVERSA(s, conversa) {
    if (!conversa || !conversa.id) return;
    const idx = s.conversas.findIndex((c) => c.id === conversa.id);
    if (idx >= 0) {
      const atual = s.conversas[idx];
      s.conversas.splice(idx, 1, {
        ...atual,
        ...conversa,
        contato: conversa.contato
          ? { ...(atual.contato || {}), ...conversa.contato }
          : atual.contato,
        estagio: conversa.estagio
          ? { ...(atual.estagio || {}), ...conversa.estagio }
          : atual.estagio,
        atendente: conversa.atendente
          ? { ...(atual.atendente || {}), ...conversa.atendente }
          : atual.atendente,
      });
    } else {
      s.conversas.unshift(conversa);
    }
  },
  REMOVE_CONVERSA(s, id) {
    s.conversas = s.conversas.filter((c) => c.id !== id);
  },
  /**
   * Atualiza dados de um contato em todas as conversas que apontam pra ele.
   * Usado quando o atendente edita o perfil — propaga pra header do drawer,
   * cards da InboxList, etc., sem precisar refetch.
   */
  UPDATE_CONTATO(s, contato) {
    if (!contato || !contato.id) return;
    s.conversas = s.conversas.map((c) => {
      if (c.contato_id !== contato.id && (!c.contato || c.contato.id !== contato.id)) return c;
      return { ...c, contato: { ...(c.contato || {}), ...contato } };
    });
  },
  SET_CONVERSA_ATIVA(s, id) { s.conversaAtivaId = id || null; },
  SET_MENSAGENS(s, { conversaId, mensagens }) {
    s.mensagensPorConversa = { ...s.mensagensPorConversa, [conversaId]: Array.isArray(mensagens) ? mensagens : [] };
  },
  PREPEND_MENSAGENS(s, { conversaId, mensagens }) {
    if (!conversaId || !Array.isArray(mensagens) || !mensagens.length) return;
    const atual = s.mensagensPorConversa[conversaId] || [];
    // Filtra duplicatas (defensivo — cursor evita, mas socket pode injetar)
    const idsAtuais = new Set(atual.map((m) => m.id));
    const novas = mensagens.filter((m) => !idsAtuais.has(m.id));
    s.mensagensPorConversa = { ...s.mensagensPorConversa, [conversaId]: [...novas, ...atual] };
  },
  LIMPAR_MENSAGENS(s, conversaId) {
    if (!conversaId) return;
    const cp = { ...s.mensagensPorConversa };
    delete cp[conversaId];
    s.mensagensPorConversa = cp;
    const cpPag = { ...s.paginacaoPorConversa };
    delete cpPag[conversaId];
    s.paginacaoPorConversa = cpPag;
  },
  SET_PAGINACAO(s, { conversaId, dados }) {
    if (!conversaId) return;
    const atual = s.paginacaoPorConversa[conversaId] || {};
    s.paginacaoPorConversa = {
      ...s.paginacaoPorConversa,
      [conversaId]: { ...atual, ...dados },
    };
  },
  ADD_MENSAGEM(s, { conversaId, mensagem }) {
    if (!conversaId || !mensagem) return;
    const atual = s.mensagensPorConversa[conversaId] || [];
    if (atual.some((m) => m.id === mensagem.id)) return;
    s.mensagensPorConversa = { ...s.mensagensPorConversa, [conversaId]: [...atual, mensagem] };
  },
  ATUALIZA_MENSAGEM_STATUS(s, { conversaId, mensagemId, status }) {
    const lista = s.mensagensPorConversa[conversaId];
    if (!lista) return;
    const idx = lista.findIndex((m) => m.id === mensagemId);
    if (idx < 0) return;
    const novo = [...lista];
    novo[idx] = { ...novo[idx], status_envio: status };
    s.mensagensPorConversa = { ...s.mensagensPorConversa, [conversaId]: novo };
  },
  // Patch parcial de midia_meta/midia_url de uma mensagem já na lista — usado
  // quando o backend gera o card de link (Open Graph) em segundo plano e emite
  // 'conversa:mensagem_atualizada'. Faz merge (não sobrescreve o resto).
  ATUALIZA_MENSAGEM_META(s, { conversaId, mensagemId, midiaMeta, midiaUrl }) {
    const lista = s.mensagensPorConversa[conversaId];
    if (!lista) return;
    const idx = lista.findIndex((m) => m.id === mensagemId);
    if (idx < 0) return;
    const novo = [...lista];
    novo[idx] = {
      ...novo[idx],
      ...(midiaMeta !== undefined ? { midia_meta: midiaMeta } : {}),
      ...(midiaUrl !== undefined ? { midia_url: midiaUrl } : {}),
    };
    s.mensagensPorConversa = { ...s.mensagensPorConversa, [conversaId]: novo };
  },
  SET_FILTRO_MINHAS(s, val) { s.filtroMinhas = Boolean(val); },
  SET_LOADING(s, val) { s.loading = Boolean(val); },
  SET_FILTRO(s, { campo, valor }) {
    s.filtros = { ...s.filtros, [campo]: valor };
  },
  RESET_FILTROS(s) {
    s.filtros = { busca: '', status: '', rotulosIds: [], estados: [] };
  },
  SET_RESPONDENDO_A(s, mensagem) { s.respondendoA = mensagem || null; },
};

export const getters = {
  estagios: (s) => s.estagios,
  conversas: (s) => s.conversas,
  conversaAtiva: (s) => s.conversas.find((c) => c.id === s.conversaAtivaId) || null,
  mensagensDaAtiva: (s) => (s.conversaAtivaId ? s.mensagensPorConversa[s.conversaAtivaId] || [] : []),
  paginacaoDaAtiva: (s) => (s.conversaAtivaId ? s.paginacaoPorConversa[s.conversaAtivaId] || {} : {}),
  filtroMinhas: (s) => s.filtroMinhas,
  filtros: (s) => s.filtros,
  respondendoA: (s) => s.respondendoA,
  loading: (s) => s.loading,

  /**
   * Quantas CONVERSAS têm pelo menos uma mensagem não-lida (1 por
   * conversa — não soma mensagens). Ignora conversas em estágio
   * finalizado. Usado pra badge no título da aba do browser
   * (igual ao "(3) WhatsApp").
   */
  naoLidasTotal: (s) => (s.conversas || []).reduce((qtd, c) => {
    if (!c) return qtd;
    const estagioTipo = c.estagio && c.estagio.tipo;
    if (estagioTipo === 'finalizado') return qtd;
    return qtd + ((Number(c.nao_lidas_count) || 0) > 0 ? 1 : 0);
  }, 0),

  /**
   * Conversas filtradas conforme filtros do kanban (busca, status, UF,
   * etiquetas). Filtragem em memória — escala bem até alguns milhares
   * de cards.
   */
  conversasFiltradas: (s) => {
    const { busca, status, estados, rotulosIds } = s.filtros;
    const buscaLower = (busca || '').toLowerCase().trim();

    return s.conversas.filter((c) => {
      const contato = c.contato || c.Contato || {};

      // Busca textual: nome, sobrenome, documento, cidade, telefone
      if (buscaLower) {
        const campos = [
          contato.nome, contato.sobrenome, contato.documento,
          contato.cidade, contato.bairro, contato.numero,
        ].filter(Boolean).map((v) => String(v).toLowerCase());
        if (!campos.some((v) => v.includes(buscaLower))) return false;
      }

      // Status do contato (ativo/inativo/bloqueado)
      if (status && contato.status !== status) return false;

      // UF
      if (estados && estados.length && !estados.includes(contato.estado)) return false;

      // Etiquetas do contato (alias 'rotulos' do Contato.associate)
      if (rotulosIds && rotulosIds.length) {
        const rotulosDoContato = (contato.rotulos || []).map((r) => r.id);
        if (!rotulosIds.some((id) => rotulosDoContato.includes(id))) return false;
      }

      return true;
    });
  },
};

export const actions = {
  async fetchEstagios({ commit }) {
    // AutoRouter expõe ConversaEstagioController em /conversa/estagio (short)
    const { data } = await axios.get('/conversa/estagio', {
      params: { where: JSON.stringify({ status: true }), order: JSON.stringify([['ordem', 'asc']]) },
    });
    commit('SET_ESTAGIOS', data);
  },

  async fetchConversas({ commit, rootGetters, state: s }) {
    commit('SET_LOADING', true);
    try {
      const where = {};
      if (s.filtroMinhas) {
        const user = rootGetters['auth/currentUser'];
        if (user && user.id) where.atendente_id = user.id;
      }
      const { data } = await axios.get('/conversa', {
        params: {
          where: JSON.stringify(where),
          // BaseController exige objeto {model,as} quando o alias da associação
          // (definido em Model.associate) não coincide com o nome do model.
          // Convenção do projeto: aliases em lowercase semântico (estagio,
          // contato, atendente) → SEMPRE enviar include como objeto.
          include: JSON.stringify([
            { model: 'ConversaEstagio', as: 'estagio' },
            // Contato com Rotulo aninhado pra filtrar por etiqueta no kanban.
            // O alias correto no Contato.associate é 'rotulos' (lowercase plural).
            {
              model: 'Contato',
              as: 'contato',
              include: [
                { model: 'Rotulo', as: 'rotulos' },
                { model: 'CanalContato', as: 'canais' },
              ],
            },
            { model: 'Usuario', as: 'atendente' },
          ]),
          order: JSON.stringify([['ultima_mensagem_em', 'desc']]),
          limit: 200,
        },
      });
      commit('SET_CONVERSAS', data || []);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async selecionarConversa({ commit, dispatch, state: s }, conversaId) {
    // Ao trocar de conversa: descarta o cache da anterior pra liberar memória
    // (e garante que reabrir busca tudo de novo, conforme requisito).
    if (s.conversaAtivaId && s.conversaAtivaId !== conversaId) {
      commit('LIMPAR_MENSAGENS', s.conversaAtivaId);
    }
    commit('SET_CONVERSA_ATIVA', conversaId);
    if (!conversaId) return;
    // Sempre fetch fresco — sem reaproveitar cache, conforme pedido
    commit('LIMPAR_MENSAGENS', conversaId);
    await dispatch('fetchMensagens', conversaId);
    // Otimista: zera badge imediato (antes mesmo do backend confirmar)
    commit('UPSERT_CONVERSA', { id: conversaId, nao_lidas_count: 0 });
    try {
      // AutoRouter expõe putMarcarLida como /conversa/marcarLida (camelCase)
      await axios.put(`/conversa/marcarLida/${conversaId}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[atendimento] marcar-lida falhou:', error.message);
    }
  },

  /**
   * Primeira página: traz as PAGE_SIZE mensagens mais recentes da conversa.
   * Backend devolve { mensagens, temMais, proximoCursor }.
   */
  async fetchMensagens({ commit }, conversaId) {
    commit('SET_PAGINACAO', { conversaId, dados: { carregando: true } });
    try {
      const { data } = await axios.get(`/conversa/${conversaId}/mensagem`, {
        params: { limit: PAGE_SIZE },
      });
      // Compatibilidade: aceita resposta nova {mensagens,...} ou array legado.
      const mensagens = Array.isArray(data) ? data : (data && data.mensagens) || [];
      const temMais = !Array.isArray(data) && Boolean(data && data.temMais);
      const cursor = !Array.isArray(data) ? (data && data.proximoCursor) || null : null;
      commit('SET_MENSAGENS', { conversaId, mensagens });
      commit('SET_PAGINACAO', { conversaId, dados: { temMais, cursor, carregando: false } });
    } catch (error) {
      commit('SET_PAGINACAO', { conversaId, dados: { carregando: false } });
      throw error;
    }
  },

  /**
   * Carrega mais PAGE_SIZE mensagens ANTERIORES ao cursor atual.
   * Chamado quando o usuário rola pra cima até o topo da lista.
   * Retorna a quantidade de mensagens carregadas (0 = fim do histórico).
   */
  async fetchMensagensAnteriores({ commit, state: s }, conversaId) {
    const id = conversaId || s.conversaAtivaId;
    if (!id) return 0;
    const pag = s.paginacaoPorConversa[id] || {};
    if (pag.carregando || pag.temMais === false) return 0;
    if (!pag.cursor) return 0;

    commit('SET_PAGINACAO', { conversaId: id, dados: { carregando: true } });
    try {
      const { data } = await axios.get(`/conversa/${id}/mensagem`, {
        params: { limit: PAGE_SIZE, before_id: pag.cursor },
      });
      const mensagens = Array.isArray(data) ? data : (data && data.mensagens) || [];
      const temMais = !Array.isArray(data) && Boolean(data && data.temMais);
      const novoCursor = !Array.isArray(data) ? (data && data.proximoCursor) || pag.cursor : pag.cursor;
      if (mensagens.length) {
        commit('PREPEND_MENSAGENS', { conversaId: id, mensagens });
      }
      commit('SET_PAGINACAO', { conversaId: id, dados: { temMais, cursor: novoCursor, carregando: false } });
      return mensagens.length;
    } catch (error) {
      commit('SET_PAGINACAO', { conversaId: id, dados: { carregando: false } });
      throw error;
    }
  },

  async enviarMensagem({ commit, state: s }, { conteudo, tipo = 'texto' }) {
    const conversaId = s.conversaAtivaId;
    if (!conversaId || !conteudo) return null;
    const payload = { conteudo, tipo };
    if (s.respondendoA && s.respondendoA.id) payload.respondendo_a_mensagem_id = s.respondendoA.id;
    const { data: mensagem } = await axios.post(`/conversa/${conversaId}/mensagem`, payload);
    commit('ADD_MENSAGEM', { conversaId, mensagem });
    commit('UPSERT_CONVERSA', { id: conversaId, ultima_mensagem: mensagem, ultima_mensagem_em: new Date().toISOString() });
    // Limpa estado de reply após envio
    commit('SET_RESPONDENDO_A', null);
    return mensagem;
  },

  setRespondendoA({ commit }, mensagem) {
    commit('SET_RESPONDENDO_A', mensagem || null);
  },

  async moverConversa({ commit }, { conversaId, estagioId }) {
    // otimista: já atualiza store; em caso de falha recarrega
    commit('UPSERT_CONVERSA', { id: conversaId, estagio_id: estagioId });
    try {
      const { data } = await axios.put(`/conversa/mover/${conversaId}`, { estagio_id: estagioId });
      commit('UPSERT_CONVERSA', data);
    } catch (error) {
      // recarrega lista se falhar
      await Promise.resolve();
      throw error;
    }
  },

  async assumirConversa({ commit }, conversaId) {
    const { data } = await axios.put(`/conversa/assumir/${conversaId}`);
    commit('UPSERT_CONVERSA', data);
  },

  async transferirConversa({ commit }, { conversaId, atendenteId }) {
    const { data } = await axios.put(`/conversa/transferir/${conversaId}`, { atendente_id: atendenteId });
    commit('UPSERT_CONVERSA', data);
  },

  async liberarConversa({ commit }, conversaId) {
    const { data } = await axios.put(`/conversa/liberar/${conversaId}`);
    commit('UPSERT_CONVERSA', data);
  },

  async registrarOptOut({ commit }, { conversaId, motivo }) {
    // AutoRouter expõe putRegistrarOptOut como /conversa/registrarOptOut
    const { data } = await axios.put(`/conversa/registrarOptOut/${conversaId}`, { motivo });
    commit('UPSERT_CONVERSA', { ...data, status_consentimento: 'opt_out' });
  },

  /**
   * Finaliza a conversa: opcionalmente envia uma mensagem final (se a
   * janela 24h estiver aberta) e move pra "Encerrado".
   * mensagem_final é opcional — sem ela a conversa só muda de estágio.
   */
  async finalizarConversa({ commit }, { conversaId, mensagemFinal = null }) {
    const { data } = await axios.put(`/conversa/finalizar/${conversaId}`, {
      mensagem_final: mensagemFinal,
    });
    commit('UPSERT_CONVERSA', data);
    return data;
  },

  async dispararTemplate(_ctx, { contatoIds, templateName, languageCode = 'pt_BR', componentes = [] }) {
    // AutoRouter expõe postDispararTemplate como /conversa/dispararTemplate
    const { data } = await axios.post('/conversa/dispararTemplate', {
      contatoIds, templateName, languageCode, componentes,
    });
    return data; // { disparados, pendentes, opt_out, falhas, detalhes }
  },

  setFiltroMinhas({ commit, dispatch }, val) {
    commit('SET_FILTRO_MINHAS', val);
    return dispatch('fetchConversas');
  },

  setFiltro({ commit }, { campo, valor }) {
    commit('SET_FILTRO', { campo, valor });
  },

  resetFiltros({ commit }) {
    commit('RESET_FILTROS');
  },

  /**
   * Verifica estado da janela 24h da Meta para um contato.
   * Retorna { aberta, ultima_entrada, expira_em, motivo, consentimento }
   */
  async verificarJanela(_ctx, contatoId) {
    const { data } = await axios.get(`/conversa/janela/${contatoId}`);
    return data;
  },

  /**
   * Busca contatos por termo (nome ou telefone). Min 2 chars.
   */
  async buscarContato(_ctx, termo) {
    if (!termo || termo.length < 2) return [];
    // AutoRouter expõe o método getBuscarContato como /conversa/buscarContato (camelCase)
    const { data } = await axios.get('/conversa/buscarContato', {
      params: { termo },
    });
    return Array.isArray(data) ? data : [];
  },

  /**
   * Lista templates HSM aprovados pela Meta para o canal informado.
   * `canalWhatsappId` é opcional — sem ele, o backend escolhe o canal
   * ativo mais recente.
   *
   * Retorna { canal, templates, aviso? } — ou só o array, se o backend
   * estiver na versão legada (compat com integrações antigas).
   */
  async fetchTemplates(_ctx, canalWhatsappId = null) {
    const params = canalWhatsappId ? { canalWhatsappId } : {};
    const { data } = await axios.get('/conversa/templates', { params });
    if (Array.isArray(data)) return { canal: null, templates: data };
    return {
      canal: data.canal || null,
      templates: Array.isArray(data.templates) ? data.templates : [],
      aviso: data.aviso || null,
    };
  },

  /**
   * Lista canais WhatsApp conectados. Útil pro modal de iniciar conversa
   * deixar o atendente escolher por qual número quer enviar.
   */
  async fetchCanaisWhatsapp() {
    const { data } = await axios.get('/canal/whatsapp');
    const lista = Array.isArray(data) ? data : (data && data.data) || [];
    return lista.filter((c) => c && c.status === 'ativo');
  },

  /**
   * Força re-sync dos templates com a Meta (POST /message/template/sincronizar/:id).
   */
  async sincronizarTemplates(_ctx, canalWhatsappId) {
    if (!canalWhatsappId) throw new Error('canalWhatsappId obrigatório.');
    const { data } = await axios.post(`/message/template/sincronizar/${canalWhatsappId}`);
    return data;
  },

  /**
   * Inicia nova conversa. Body:
   *   { contato_id?, telefone?, nome?,
   *     modo: 'texto' | 'template',
   *     conteudo? | templateName? + templateValores? }
   */
  async iniciarConversa({ commit }, payload) {
    const { data } = await axios.post('/conversa/iniciar', payload);
    if (data && data.conversa) {
      const conversaComPrevia = data.mensagem
        ? { ...data.conversa, ultima_mensagem: data.mensagem }
        : data.conversa;
      commit('UPSERT_CONVERSA', conversaComPrevia);
      commit('SET_CONVERSA_ATIVA', data.conversa.id);
    }
    return data;
  },

  /**
   * Atualiza os dados do contato (PUT /contato/:id) e propaga via mutation
   * pra todas as conversas no store — mantém header do drawer, cards da
   * InboxList e demais views observando reativamente os campos editados.
   */
  async atualizarContato({ commit }, { id, dados }) {
    const { data } = await axios.put(`/contato/${id}`, dados);
    const novo = (data && data.data) || data;
    if (novo && novo.id) commit('UPDATE_CONTATO', novo);
    return novo;
  },

  /**
   * Abre/cria a conversa whatsapp de um contato existente sem enviar
   * mensagem. Aceita `{ contatoId?, telefone? }` — o telefone serve como
   * fallback robusto pra evitar duplicidade quando há contatos duplicados
   * com variações do mesmo número (com/sem o "9" do celular BR).
   *
   * Pode retornar `{ conflito: true, atendente: {id,nome}, conversa }`
   * quando a conversa existente está com outro atendente — nesse caso
   * NÃO selecionamos a conversa (caller decide o que fazer).
   */
  async abrirConversaCom({ commit }, payload) {
    const body = typeof payload === 'object'
      ? { contato_id: payload.contatoId, telefone: payload.telefone }
      : { contato_id: payload };
    const { data } = await axios.post('/conversa/abrir', body);
    if (data && data.conflito) return data; // não atualiza estado — caller mostra modal
    if (data && data.conversa) {
      commit('UPSERT_CONVERSA', data.conversa);
      commit('SET_CONVERSA_ATIVA', data.conversa.id);
    }
    return data;
  },

  /**
   * Eventos de socket (chamado pelos handlers em socketClient).
   */
  socketNovaMensagem({ commit, state: s }, payload) {
    if (!payload) return;
    const conversaId = (payload.conversa && payload.conversa.id) || (payload.mensagem && payload.mensagem.conversa_id);
    if (!conversaId) return;
    if (payload.mensagem) {
      commit('ADD_MENSAGEM', { conversaId, mensagem: payload.mensagem });
    }
    if (payload.conversa) {
      // Anexa a mensagem como ultima_mensagem para a prévia do card
      // atualizar live (estilo WhatsApp Web).
      const conversaComPrevia = payload.mensagem
        ? { ...payload.conversa, ultima_mensagem: payload.mensagem }
        : payload.conversa;
      commit('UPSERT_CONVERSA', conversaComPrevia);
    } else if (payload.mensagem) {
      // Sem o objeto conversa completo no payload, ainda assim atualiza
      // a prévia do card pra mensagem nova.
      commit('UPSERT_CONVERSA', { id: conversaId, ultima_mensagem: payload.mensagem });
    }
    // Se a conversa não está aberta, mantém badge; se está aberta, zera.
    if (s.conversaAtivaId === conversaId) {
      commit('UPSERT_CONVERSA', { id: conversaId, nao_lidas_count: 0 });
    }

    // Som de notificação — só pra mensagens ENTRANTES, e só quando a
    // conversa não está aberta OU a aba está em background. Política
    // igual WhatsApp Web: ver a conversa em foco = silêncio.
    const msg = payload.mensagem;
    if (msg && msg.direcao === 'entrada') {
      const conversaAtivaNaTela = s.conversaAtivaId === conversaId;
      const abaEmFoco = typeof document !== 'undefined' && !document.hidden;
      if (!conversaAtivaNaTela || !abaEmFoco) {
        notificationSound.playNova();
      }
    }
  },

  socketConversaAtualizada({ commit }, conversa) {
    if (!conversa || !conversa.id) return;
    commit('UPSERT_CONVERSA', conversa);
  },

  socketMensagemStatus({ commit }, { mensagem_id, conversa_id, status }) {
    if (!mensagem_id || !conversa_id) return;
    commit('ATUALIZA_MENSAGEM_STATUS', { conversaId: conversa_id, mensagemId: mensagem_id, status });
  },

  // Backend gerou o card de link (ou outra atualização parcial de mídia) após a
  // mensagem já ter sido entregue → faz merge no item já renderizado.
  socketMensagemAtualizada({ commit }, { mensagem_id, conversa_id, midia_meta, midia_url }) {
    if (!mensagem_id || !conversa_id) return;
    commit('ATUALIZA_MENSAGEM_META', {
      conversaId: conversa_id,
      mensagemId: mensagem_id,
      midiaMeta: midia_meta,
      midiaUrl: midia_url,
    });
  },
};
